/**
 * threadArrival — when does movement on a thread's `lastActiveAt` count as a
 * LIVE arrival worth a chime and a badge, and how many unread messages does a
 * thread really have from the viewer's seat (2026-09-08, owner report: "each
 * time I refresh the page all threads get a new notification 1 even if the
 * notification went away in viewing the thread").
 *
 * The mechanism behind that report: the thread store's `ready` flips after
 * the LOCALSTORAGE hydrate, so the background watcher's one baseline pass saw
 * yesterday's copy of the rail. When the Firestore index snapshot landed a
 * moment later — with the server's fresher `lastActiveAt`, or a thread the
 * local copy never had — every one of those read as a live arrival: chime,
 * `markUnread(+1)`, and the +1 was WRITTEN to the index doc, so the phantom
 * came back on every device and every reload until each thread was opened.
 *
 * Two facts settle it, both pure:
 *   • a thread the viewer has VIEWED SINCE its last activity has nothing
 *     unseen — whatever a stale counter says (`isViewedSince`,
 *     `effectiveUnreadCount`);
 *   • activity older than the live window is HISTORY the rail already shows
 *     through `hasUnseenActivity`'s quiet dot — never a fresh arrival
 *     (`isLiveArrival`). A message that genuinely lands while the tab is open
 *     carries a server stamp seconds old and still notifies; so does the first
 *     message of a thread someone just added the viewer to (the 2026-08-27
 *     review fix stands).
 */
import type { ChatThread } from './types';

/** How old a `lastActiveAt` may be and still count as "just happened". */
export const LIVE_ARRIVAL_WINDOW_MS = 90_000;

function millis(iso: string | null | undefined): number {
  if (typeof iso !== 'string' || !iso) return NaN;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/** True when the viewer opened the thread at or after its last activity. */
export function isViewedSince(thread: Pick<ChatThread, 'lastActiveAt' | 'lastViewedAt'>): boolean {
  const active = millis(thread?.lastActiveAt);
  const viewed = millis(thread?.lastViewedAt);
  if (!Number.isFinite(active)) return true; // nothing datable to be unseen
  if (!Number.isFinite(viewed)) return false;
  return viewed >= active;
}

/** True when `lastActiveAt` is within the live window of `nowMs` (clock skew forward counts as live). */
export function isLiveArrival(lastActiveAt: string | null | undefined, nowMs: number): boolean {
  const active = millis(lastActiveAt);
  if (!Number.isFinite(active)) return false;
  return nowMs - active <= LIVE_ARRIVAL_WINDOW_MS;
}

/**
 * The unread count the rail should SHOW: the stored counter, unless the viewer
 * PROVABLY viewed the thread since its last activity (both stamps datable,
 * viewed ≥ active) — then zero, whatever a phantom increment wrote. Never
 * raises the count, and never hides one it cannot date: a count on a thread
 * with no usable activity stamp stands (the watcher cannot have minted it —
 * it needs a datable stamp inside the live window — so it is not the phantom).
 */
export function effectiveUnreadCount(
  thread: Pick<ChatThread, 'unreadCount' | 'lastActiveAt' | 'lastViewedAt'>,
): number {
  const stored = typeof thread?.unreadCount === 'number' && Number.isFinite(thread.unreadCount)
    ? Math.max(0, Math.floor(thread.unreadCount))
    : 0;
  if (stored === 0) return 0;
  const active = millis(thread?.lastActiveAt);
  const viewed = millis(thread?.lastViewedAt);
  if (Number.isFinite(active) && Number.isFinite(viewed) && viewed >= active) return 0;
  return stored;
}

export type BackgroundArrivalVerdict =
  | 'baseline'
  | 'unchanged'
  | 'active'
  | 'own'
  | 'viewed'
  | 'history'
  | 'recent-send'
  | 'muted'
  | 'notify';

/**
 * The background watcher's decision for one thread, in the order the watcher
 * applies it (kept in lockstep by threadActivityBadgeWiring.test.tsx). Pure,
 * so the refresh scenario is a unit test.
 */
export function judgeBackgroundArrival(args: {
  firstPass: boolean;
  prevLastActiveAt: string | undefined;
  thread: Pick<ChatThread, 'id' | 'lastActiveAt' | 'lastViewedAt' | 'lastActivityBy'>;
  activeThreadId: string | null;
  uid: string;
  nowMs: number;
  sentRecently: boolean;
  mode: 'off' | 'chime' | 'chime-platform';
}): BackgroundArrivalVerdict {
  const { thread } = args;
  const last = typeof thread.lastActiveAt === 'string' ? thread.lastActiveAt : '';
  if (args.firstPass) return 'baseline';
  if (args.prevLastActiveAt !== undefined && last <= args.prevLastActiveAt) return 'unchanged';
  if (thread.id === args.activeThreadId) return 'active';
  if ((thread.lastActivityBy ?? undefined) === args.uid) return 'own';
  if (isViewedSince(thread)) return 'viewed';
  if (!isLiveArrival(thread.lastActiveAt, args.nowMs)) return 'history';
  if (args.sentRecently) return 'recent-send';
  if (args.mode === 'off') return 'muted';
  return 'notify';
}
